import React, { useEffect, useMemo, useRef, useState } from 'react';
import StickyFooter from '@lwt-helix/sticky-footer';
import ShowAdditionalInfo from '../../common/components/table/ShowAdditionalInfo';
import Tab from '@lwt-helix/tab';
import { setStateData } from '../../common/helpers/state';
import { useSearchStore } from '../../store/store';
import { agentProductionTerms as apt, searchTerms } from '../../constants';
import { useMarketDynamicsStore } from '../../store/marketDynamics/store';
import {
    getRowsWithData,
    getDefaultSortColumnSettings,
    getRevisedOfficeBreakdownColumns,
    buildAdditionalInfoCols
} from '../helpers/agGrid';
import { getUpdatedObjectByKeyfield } from '../../common/helpers/utilities';
import { yearMonthDayDashDateFormat } from '../../agent-production/helpers/dataFormatters';
import { getBreakdownTooltips } from '../constants/tooltipDefinitions';
import { getSummaryPinnedConfig } from '../../common/components/table/agGrid/pinnedRowSettings';
import { responseKeys } from '../../constants/service';
import { useHistory, useLocation } from 'react-router-dom';
import { getBreakdownRoute, officeListingsRoute } from '../constants/routes';
import ListingsBreakdownTable from './ListingsBreakdown';
import { getListingTabsByStatus, listingBreakdownViewsIds } from '../constants/listingViews';
import { getTransformData } from '../helpers/helpers';
import { exportTypes } from '../helpers/export';
import { getExportButton } from '../../common/helpers/export';
import { exportButtonTypes } from '../../common/constants/export';
import { BreakdownTable as OfficesBreakdownTable } from './common/BreakdownTable';
import { breakdownTabs } from '../../store/marketDynamics';

const Breakdowns = () => {
    const gridRefs = useRef([]);
    const location = useLocation();
    const {
        searchCriteria,
        timeFrameTitle,
        summaryPinnedData,
        setTotalsRowData,
        setSummaryPinnedData,
        selectedBreakdownTab,
        setSelectedBreakdownTab,
        shouldShowAdditionalInfo,
        isFooterDisabled,
        setIsFooterDisabled,
        resetDisabledFooterTabs,
        listingStatusLabel,
    } = useSearchStore(state => {
        const { [state.selectedModule]: moduleState } = state;
        const { breakdowns } = moduleState;
        const { selectedBreakdownTab, tabsByRoute } = breakdowns;
        const breakdownTabData = tabsByRoute[location.pathname]?.[selectedBreakdownTab];
        return ({
            searchCriteria: moduleState?.search?.searchCriteria,
            timeFrameTitle: moduleState?.timeFrameTitle,
            summaryPinnedData: moduleState?.summaryPinnedData,
            setTotalsRowData: state.setTotalsRowData,
            setSummaryPinnedData: state.setSummaryPinnedData,
            selectedBreakdownTab,
            setSelectedBreakdownTab: state.setSelectedBreakdownTab,
            shouldShowAdditionalInfo: breakdownTabData?.shouldShowAdditionalInfo,
            isFooterDisabled: breakdownTabData?.isFooterDisabled,
            setIsFooterDisabled: state.setIsFooterDisabled,
            resetDisabledFooterTabs: state.resetDisabledFooterTabs,
            listingStatusLabel: breakdownTabData?.listingStatus?.label,
        });
    });
    const {
        checkedStatusOptions,
        getOfficesBreakdown,
        selectedBreakdownPeriod,
        tableView,
        selectedListingView,
    } = useMarketDynamicsStore(state => ({
        checkedStatusOptions: state.checkedStatusOptions,
        getOfficesBreakdown: state.getOfficesBreakdown,
        selectedBreakdownPeriod: state.selectedBreakdownPeriod,
        tableView: state.tableView,
        selectedListingView: state.selectedListingView,
        selectedBreakdownTab: state.selectedBreakdownTab,
    }));
    const [state, setState] = useState({
        breakdownTooltips: {},
        offices: [],
        revisedSearchCriteria: null,
        isLoading: true,
    });

    const history = useHistory();

    const isOfficeListing = location.pathname === officeListingsRoute;

    const revisedTableView = useMemo(() => {
        const revisedColumnDefs = getRevisedOfficeBreakdownColumns(tableView);
        const additionalColumnsColDefs = shouldShowAdditionalInfo
            ? buildAdditionalInfoCols(revisedColumnDefs)
            : revisedColumnDefs;
        return {
            ...tableView,
            tables: additionalColumnsColDefs,
            defaultSort: getDefaultSortColumnSettings(revisedColumnDefs, tableView.id)
        };
    }, [tableView, state.offices, shouldShowAdditionalInfo]);

    const revisedOfficesData = useMemo(() => {
        if (!revisedTableView || !state.offices?.results) return [];
        return getRowsWithData(getTransformData(state.offices?.results), revisedTableView.tables);
    }, [state.offices, revisedTableView]);

    const summaryPinnedConfig = getSummaryPinnedConfig(tableView.id);

    const isOfficeBreakdownTab = !isOfficeListing && selectedBreakdownTab === breakdownTabs.OFFICE;

    const exportDetails = {
        exportType: isOfficeBreakdownTab ? exportTypes.mdOfficeBreakdown : exportTypes.mdListingsBreakdown,
        listingType: selectedListingView,
        listingStatus: !isOfficeBreakdownTab ? listingStatusLabel : '',
    };

    const exportButton = getExportButton({
        exportDetails,
        gridRef: gridRefs.current[isOfficeListing ? (selectedBreakdownTab + 1) : selectedBreakdownTab],
        type: exportButtonTypes.primary,
        isDisabled: isFooterDisabled,
        isTabExport: true,
    })

    const columnProps = [
        {
            children: (
                <div className='ml-auto d-flex w-auto align-self-center'>
                    <ShowAdditionalInfo />
                    {exportButton}
                </div>
            ),
            sm: 12,
            align: 'right'
        }
    ];

    const setCurrentTab = (currentTab) => {
        if (!isOfficeListing) history.push(getBreakdownRoute(currentTab));
        setSelectedBreakdownTab(currentTab);
    };

    const listingStatusTabs = listingBreakdownViewsIds.includes(tableView?.id)
        ? checkedStatusOptions.map((status, index) =>  ({
            title: `${apt.listings} (${status.label})`,
            content: {
                jsx: <ListingsBreakdownTable
                    gridRef={(ref) => (gridRefs.current[index + 1] = ref)}
                    tabIndex={index + 1}
                    listingStatus={status}
                    tableTitleWithTooltips={state.breakdownTooltips}
                    searchCriteria={state.revisedSearchCriteria}
                    isOfficeListing={isOfficeListing} />
            }
        }))
        : getListingTabsByStatus({
            selectedListingViewId: tableView?.id,
            tableTitleWithTooltips: state.breakdownTooltips,
            searchCriteria: state.revisedSearchCriteria,
            component: ListingsBreakdownTable,
            checkedStatusOptions,
            gridRefs,
            isOfficeListing
        });

    useEffect(() => {
        if (!searchCriteria || !selectedBreakdownPeriod) return;
        const timePeriodParameter = {
            intervalType: "Custom",
            startDate: yearMonthDayDashDateFormat(selectedBreakdownPeriod.timePeriodStart),
            endDate: yearMonthDayDashDateFormat(selectedBreakdownPeriod.timePeriodEnd)
        };
        const revisedSearchCriteria = getUpdatedObjectByKeyfield(searchCriteria, 'timePeriod', timePeriodParameter);
        const getOffices = async () => {
            resetDisabledFooterTabs(location.pathname);
            const offices = await getOfficesBreakdown(revisedSearchCriteria);
            setTotalsRowData(getTransformData(offices?.totals), responseKeys.totals);
            setSummaryPinnedData(tableView.id, responseKeys.totals);
            setState(state => ({
                ...state,
                offices,
                revisedSearchCriteria,
                isLoading: false,
            }));
        };
        getOffices();
    }, [searchCriteria, selectedBreakdownPeriod]);

    useEffect(() => {
        setIsFooterDisabled(breakdownTabs.OFFICE, state.isLoading, location.pathname);
    }, [state.isLoading, isOfficeListing, location.pathname]);

    useEffect(() => {
        if (!timeFrameTitle) return;
        const breakdownTooltips = getBreakdownTooltips(timeFrameTitle.mainTitle, tableView.id);
        setStateData('breakdownTooltips', breakdownTooltips, setState);
    }, [timeFrameTitle]);

    const officeBreakdownProps = {
        gridRef: (ref) => (gridRefs.current[breakdownTabs.OFFICE] = ref),
        tableView: revisedTableView,
        rowData: revisedOfficesData,
        tableTitleWithTooltips: state.breakdownTooltips,
        pinnedBottomRowData: summaryPinnedConfig && [{...summaryPinnedData }],
        externalParams: { summaryPinnedConfig },
        isLoading: state.isLoading,
    };

    return (
        <div className='px-3 office-breakdown-wrapper'>
            <h4 className='title-row text-truncate mb-0 font-gray-primary font-weight-600'>
                {selectedListingView}
            </h4>
            <Tab
                className='mt-4 mb-4'
                tabTextTransform='none'
                navSize={checkedStatusOptions.length}
                contentSize={checkedStatusOptions.length}
                items={isOfficeListing
                    ? [...listingStatusTabs]
                    : [
                        {
                            title: searchTerms.offices,
                            content: { jsx: <OfficesBreakdownTable { ...officeBreakdownProps } /> }
                        },
                        ...listingStatusTabs]}
                tabs
                currentIndex={selectedBreakdownTab}
                setCurrentIndex={(index) => setCurrentTab(index)}
            />
            <StickyFooter columnProps={columnProps}/>
        </div>
    );
};

export default Breakdowns;
